/**
 * The only thing an unauthenticated visitor can see.
 *
 * The bundle itself is public — it has to be, since it *is* the login page —
 * but it holds no data. Everything on the dashboard comes from an API call, and
 * every one of those is behind the session. So a visitor without the password
 * gets this box and nothing else: no GPU model, no VRAM, no model names.
 */
import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError, signIn } from "./api";

export default function LoginPage() {
  const [password, setPassword] = useState("");
  const queryClient = useQueryClient();

  const attempt = useMutation({
    mutationFn: () => signIn(password),
    onSuccess: () => {
      setPassword("");
      // Drop every cached 401 so the dashboard's queries re-run with the
      // cookie the browser now holds.
      void queryClient.invalidateQueries();
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (password && !attempt.isPending) attempt.mutate();
  }

  const error = attempt.error as ApiError | null;
  const wrongPassword = error?.status === 401;

  return (
    <div className="login-shell">
      <form className="login" onSubmit={onSubmit}>
        <div className="login-brand">
          <div className="mk">M</div>
          <div>
            <b>Model Serving</b>
            <span>models.os.agintics.ai</span>
          </div>
        </div>

        <p className="login-lede">
          This console manages the models on your machines. Sign in to
          continue.
        </p>

        <label className="login-field" htmlFor="password">
          Password
          <input
            id="password"
            type="password"
            value={password}
            autoFocus
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            disabled={attempt.isPending}
          />
        </label>

        {error && (
          <p className={wrongPassword ? "login-error" : "login-error muted"}>
            {wrongPassword ? "That password is not right." : error.detail}
          </p>
        )}

        <button
          className="btn primary login-submit"
          type="submit"
          disabled={!password || attempt.isPending}
        >
          {attempt.isPending ? "Checking…" : "Sign in"}
        </button>

        <p className="login-foot">
          The password is set in the server&rsquo;s <code>.env</code>. A
          generated one is printed to its console on first start.
        </p>
      </form>
    </div>
  );
}
